import {
  type StorageObject,
  useGetDownloadSignedUrlForBlockedRecordQuery,
} from '@exam-center/graphql';
import { useEffect, useMemo, useState } from 'react';

import { getContentInfoFromSignedUrl } from './utils';

interface FileWithInfo {
  name: string;
  type: string;
  size: number;
  value: {
    url: string;
    name: string;
  };
}

type BlockedRecordStorageObject = Pick<StorageObject, 'fileName' | 'key'>;

interface UseGetBlockedRecordDownloadSignedUrlWithInfoProps {
  blockedRecordId: string;
  blockedRecordStorageObjects: BlockedRecordStorageObject[];
  options?: {
    skip?: boolean;
  };
}
export function useGetBlockedRecordDownloadSignedUrlWithInfo(
  params: Readonly<UseGetBlockedRecordDownloadSignedUrlWithInfoProps>,
) {
  const {
    blockedRecordId,
    blockedRecordStorageObjects,
    options: { skip: shouldSkip = false } = {},
  } = params;

  const [files, setFiles] = useState<FileWithInfo[]>([]);

  const fileNames = useMemo(
    () => blockedRecordStorageObjects?.map((file) => file.key.split('/').pop() ?? '') ?? [],
    [blockedRecordStorageObjects],
  );

  const { data } = useGetDownloadSignedUrlForBlockedRecordQuery({
    variables: {
      getDownloadSignedUrlForBlockedRecordInput: {
        blockedRecordId,
        fileNames,
      },
    },
    fetchPolicy: 'no-cache',
    skip: shouldSkip,
  });

  useEffect(() => {
    if (!blockedRecordStorageObjects || !fileNames) {
      setFiles([]);
      return;
    }
    const getFiles = async () => {
      const files = await Promise.all(
        blockedRecordStorageObjects.map(async (file, idx) => {
          const fileName = fileNames[idx];
          const url = data?.ecGetDownloadSignedUrlForBlockedRecord.signedUrls[fileName];
          const { type, size } = await getContentInfoFromSignedUrl(url);
          return {
            name: file.fileName ?? '-',
            size: size,
            type,
            value: {
              url,
              name: fileName ?? '',
            },
          };
        }),
      );
      setFiles(files);
    };
    getFiles();
  }, [blockedRecordStorageObjects, fileNames, data]);

  return files;
}
